/**
 * DiagnosticsPage — full-detail strategy diagnostics.
 *
 * Renders deep diagnostics for one strategy at a time, selected via the
 * top-level strategy toggle. Today only Vyasa is wired; Cobra is a
 * placeholder for the equivalent qCobra view to follow.
 *
 * The diagnostics surface differs in intent from qCockpit:
 *   - qCockpit answers "is the strategy running, am I losing money?"
 *   - Diagnostics answers "why did/didn't the model fire, what's stale,
 *     what does each leg look like in full detail?"
 *
 * That makes diagnostics intentionally denser, table-heavier, and
 * monospace where helpful for inspection.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
} from '@mui/material';
import VyasaDiagnostics from './VyasaDiagnostics';

const STRATEGY_OPTIONS = [
    { value: 'vyasa',    label: 'Vyasa',    enabled: true,  caption: 'ML 4-combo unified-book options buying' },
    { value: 'cobra',    label: 'qCobra',   enabled: false, caption: 'V1 (legacy) · V2 (regime) · V3 (regime+) · V4 (ML)' },
    { value: 'chanakya', label: 'Chanakya', enabled: false, caption: 'NIFTY RSI long-options' },
];

const STORAGE_KEY = 'diagnostics:strategy';

const DiagnosticsPage = () => {
    const [strategy, setStrategy] = useState(() => {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY);
            return STRATEGY_OPTIONS.find((o) => o.value === saved && o.enabled)?.value || 'vyasa';
        } catch (_) {
            return 'vyasa';
        }
    });

    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, strategy); } catch (_) {}
    }, [strategy]);

    const handleStrategyChange = (_event, next) => {
        if (!next) return;
        const opt = STRATEGY_OPTIONS.find((o) => o.value === next);
        if (!opt || !opt.enabled) return;
        setStrategy(next);
    };

    const selectedOption = useMemo(
        () => STRATEGY_OPTIONS.find((o) => o.value === strategy) || STRATEGY_OPTIONS[0],
        [strategy],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minHeight: 0 }}>
            <Box>
                <Typography
                    variant="overline"
                    sx={{ fontWeight: 700, letterSpacing: '0.14em', color: 'text.secondary' }}
                >
                    Strategy Diagnostics
                </Typography>
                <Typography
                    variant="h4"
                    sx={{ mt: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}
                >
                    {selectedOption.label} — Detail
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 920 }}>
                    Full-detail per-strategy diagnostics. Toggle between strategies to inspect their state, leg
                    details, decision history, and event stream metadata. Polls at 1 Hz where data is live.
                </Typography>
            </Box>

            <ToggleButtonGroup
                value={strategy}
                exclusive
                onChange={handleStrategyChange}
                size="small"
                sx={{ flexWrap: 'wrap' }}
            >
                {STRATEGY_OPTIONS.map((opt) => (
                    <ToggleButton
                        key={opt.value}
                        value={opt.value}
                        disabled={!opt.enabled}
                        sx={{ textTransform: 'none', px: 2, py: 0.6 }}
                    >
                        <Stack alignItems="flex-start" spacing={0}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                {opt.label}{!opt.enabled ? ' (soon)' : ''}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.66rem', textTransform: 'none', lineHeight: 1.2 }}
                            >
                                {opt.caption}
                            </Typography>
                        </Stack>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Box sx={{ minHeight: 0 }}>
                {strategy === 'vyasa' ? (
                    <VyasaDiagnostics />
                ) : (
                    <Alert severity="info" variant="outlined">
                        Diagnostics for <strong>{selectedOption.label}</strong> are not wired yet. The same shape will
                        be built once Vyasa diagnostics stabilize.
                    </Alert>
                )}
            </Box>
        </Box>
    );
};

export default DiagnosticsPage;
